import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Col, Glyphicon, Grid, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import Modal from 'react-modal'
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { useT } from '@transifex/react'
import axios from 'axios'
import { swalError } from '../../common/components/Swaler'
import { submitLocksAndUsers } from '../../common/redux/collectionActions'

import NewUserCompany from './NewUserCompany'

import './Modal.css'
import translationMappings, { countryPhoneFormatMapping } from '../../transifex/translationMappings'
import HandleStorageSupplierClientsModal from './HandleStorageSupplierClientsModal'

const checkRemoteAccessOption = (lock) => lock.qiot

const checkRemoteCarrierDoorAccessOption = (lock) => {
  if (lock.sub_type !== 'container') return false

  return lock.carrier_door?.qiot
}

const defaultCountry = 'SE'
const permissionOptions = ['deliveries', 'carrierDoorAccess', 'remoteCarrierDoorAccess', 'rental', 'remoteAccess', 'storageModifyInventory', 'storageCreateOrder']

const customStyles = {
  content: {
    position: 'fixed',
    top: '10%',
    left: '12%',
    right: 'auto',
    bottom: 'auto',
    padding: 20,
    zIndex: 9999,
    width: '85%',
    height: '80%',
    backgroundColor: 'white',
    border: '1px solid lightgrey',
  },
}

const NewUserModal = ({ predefinedSubject, rentalCompanies, locks, closeModal, modalIsOpen }) => {
  const t = useT()
  const [users, setUsers] = useState([])
  const [selectedLocks, setSelectedLocks] = useState([])
  const [selectedCompany, setSelectedCompany] = useState(localStorage.newUserPreselectedCompany ? JSON.parse(localStorage.newUserPreselectedCompany) : { label: '', value: '' })
  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState(defaultCountry)
  const [alternativePhone, setAlternativePhone] = useState('')
  const [isHandleStorageSupplierClientsModalOpen, setIsHandleStorageSupplierClientsModalOpen] = useState('')
  const [storageSuppliers, setStorageSuppliers] = useState(null)
  const dispatch = useDispatch()

  const keyedLocks = useSelector((state) => state.collectionData.keyedLocks)
  useEffect(() => {
    const { customer } = predefinedSubject

    if (customer) {
      const p = `+${customer.countryCode || '46'}${(customer.phone || '').replace(/^0/g, '')}`

      const { country: c } = parsePhoneNumber(phone || '') || { country: defaultCountry }

      setCountry(c)
      setPhone(p)
      setSelectedCompany({
        value: customer.company._id,
        label: customer.company.name,
      })
      setAlternativePhone(customer.alternative_phone)
      setSelectedLocks(customer.locks)
    }
  }, [])

  const addUser = () => {
    if (phone && selectedCompany.value) {
      const { countryCallingCode } = parsePhoneNumber(phone) || { countryCallingCode: '46' }

      const user = {
        phone,
        countryCode: countryCallingCode,
        selectedCompany,
      }

      const newUsers = [...users, user]

      setUsers(newUsers)
      setPhone('')
      setAlternativePhone('')

      return newUsers
    }

    return users
  }

  const removeUser = (user) => {
    const newUsers = users.filter((u) => u.phone !== user.phone)

    setUsers(newUsers)
  }
  const toggleLock = (lock) => {
    const lockToRemove = selectedLocks.find((f) => f._id === lock._id)
    const rentalCompany = lock.rental_company !== undefined && lock.rentalActive !== false && rentalCompanies.find((c) => c._id === lock.rental_company)

    if (lockToRemove) {
      const newSelectedLocks = selectedLocks.filter((f) => f._id !== lock._id)

      setSelectedLocks(newSelectedLocks)
    } else if (lock.type === 'passage' && lock.storage) {
      setSelectedLocks([...selectedLocks, { ...lock, permissions: { storageCreateOrder: true } }])
    } else if (rentalCompany?.active !== false) {
      setSelectedLocks([...selectedLocks, { ...lock, permissions: { rental: true } }])
    } else {
      setSelectedLocks([...selectedLocks, lock])
    }
  }

  const submit = async () => {
    const usersToAdd = addUser()

    if (usersToAdd.length === 0) {
      swalError('You have to add att least one user')
    } else if (selectedLocks.length === 0) {
      swalError('You have to add att least one unit')
    } else {
      const locksToAdd = selectedLocks.map((lock) => ({ _id: lock._id, permissions: lock.permissions }))

      dispatch(
        submitLocksAndUsers(
          {
            locks: locksToAdd,
            users: usersToAdd.map((user) => ({
              ...user,
              companyId: user.selectedCompany.value,
            })),
          },
          (error) => {
            if (error) {
              if (error.response?.data?.suppliers) {
                setIsHandleStorageSupplierClientsModalOpen(true)
                setStorageSuppliers(error.response?.data?.suppliers)
              }
            } else {
              localStorage.newUserPreselectedCompany = JSON.stringify(selectedCompany)

              closeModal()
            }
          }
        )
      )
    }
  }

  const submitNewSuppliers = async (suppliers) => {
    setIsHandleStorageSupplierClientsModalOpen(false)
    await axios.put(`${window.MYQLOCX_API}/subject/admin/locks/storage/suppliers`, { locks: selectedLocks.map((l) => l._id), suppliers })
  }

  const permissionIsToggled = (lockId, permission) => {
    const lock = selectedLocks.find((l) => l._id === lockId)
    if (!lock) return false

    return lock.permissions && lock.permissions[permission] === true
  }

  const togglePermission = (lock, permission) => {
    const selectedLock = selectedLocks.find((f) => f._id === lock._id)

    if (selectedLock) {
      const newSelectedLocks = selectedLocks.map((l) => {
        const modifiedLock = { ...l }

        if (l._id === lock._id) {
          if (!modifiedLock.permissions) modifiedLock.permissions = {}

          modifiedLock.permissions[permission] = !modifiedLock.permissions[permission]
        }

        return modifiedLock
      })

      setSelectedLocks(newSelectedLocks)
    } else {
      setSelectedLocks([...selectedLocks, lock])
    }
  }

  const renderPermissions = (lock) => {
    const permissions = []

    permissionOptions.forEach((permission) => {
      if (lock.type !== 'passage' && lock.type !== 'cluster' && permission === 'deliveries') {
        permissions.push(
          <li key={permission} style={{ textDecoration: 'none', cursor: 'pointer' }}>
            {t(translationMappings[permission])}
            <Glyphicon glyph="glyphicon glyphicon-check" style={{ color: '#006e78', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
          </li>
        )
      }
      if (lock.rental_company !== undefined && lock.rentalActive !== false && permission === 'rental') {
        const rentalCompany = rentalCompanies.find((c) => c._id === lock.rental_company)

        if (rentalCompany && rentalCompany.active !== false) {
          permissions.push(
            <li key={permission} onClick={() => togglePermission(lock, permission)} style={{ textDecoration: 'none', cursor: 'pointer' }}>
              {t(translationMappings[permission])}
              {permissionIsToggled(lock._id, permission) ? (
                <Glyphicon glyph="glyphicon glyphicon-check" style={{ color: '#006e78', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
              ) : (
                <Glyphicon glyph="glyphicon glyphicon-unchecked" style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
              )}
            </li>
          )
        }
      }
      if (lock.storage !== undefined && permission === 'storageModifyInventory') {
        permissions.push(
          <li key={permission} onClick={() => togglePermission(lock, permission)} style={{ textDecoration: 'none', cursor: 'pointer' }}>
            {t(translationMappings[permission])}

            {permissionIsToggled(lock._id, permission) ? (
              <Glyphicon glyph="glyphicon glyphicon-check" style={{ color: '#006e78', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
            ) : (
              <Glyphicon glyph="glyphicon glyphicon-unchecked" style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
            )}
          </li>
        )
      }
      if (lock.storage !== undefined && permission === 'storageCreateOrder') {
        permissions.push(
          <li key={permission} style={{ textDecoration: 'none', cursor: 'pointer' }}>
            {t(translationMappings[permission])}
            <Glyphicon glyph="glyphicon glyphicon-check" style={{ color: '#006e78', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
          </li>
        )
      }
      if (lock.type === 'cluster' && permission === 'allowClusterBooking') {
        permissions.push(
          <li key={permission} onClick={() => togglePermission(lock, permission)} style={{ textDecoration: 'none', cursor: 'pointer' }}>
            {t(translationMappings[permission])}

            {permissionIsToggled(lock._id, permission) ? (
              <Glyphicon glyph="glyphicon glyphicon-check" style={{ color: '#006e78', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
            ) : (
              <Glyphicon glyph="glyphicon glyphicon-unchecked" style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
            )}
          </li>
        )
      }
      if (permission === 'remoteAccess' && checkRemoteAccessOption(lock)) {
        permissions.push(
          <li key={permission} onClick={() => togglePermission(lock, permission)} style={{ textDecoration: 'none', cursor: 'pointer' }}>
            {t(translationMappings[permission])}

            {permissionIsToggled(lock._id, permission) || permissionIsToggled(lock._id, 'contactPerson') ? (
              <Glyphicon glyph="glyphicon glyphicon-check" style={{ color: '#006e78', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
            ) : (
              <Glyphicon glyph="glyphicon glyphicon-unchecked" style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
            )}
          </li>
        )
      }
      if (permission === 'remoteCarrierDoorAccess' && lock.carrier_door && checkRemoteCarrierDoorAccessOption(lock)) {
        permissions.push(
          <li key={permission} onClick={() => togglePermission(lock, permission)} style={{ textDecoration: 'none', cursor: 'pointer' }}>
            {t(translationMappings[permission])}

            {permissionIsToggled(lock._id, permission) || permissionIsToggled(lock._id, 'contactPerson') ? (
              <Glyphicon glyph="glyphicon glyphicon-check" style={{ color: '#006e78', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
            ) : (
              <Glyphicon glyph="glyphicon glyphicon-unchecked" style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
            )}
          </li>
        )
      }
      if (permission === 'carrierDoorAccess' && lock.carrier_door) {
        permissions.push(
          <li key={permission} onClick={() => togglePermission(lock, permission)} style={{ textDecoration: 'none', cursor: 'pointer' }}>
            {t(translationMappings[permission])}

            {permissionIsToggled(lock._id, permission) || permissionIsToggled(lock._id, 'contactPerson') ? (
              <Glyphicon glyph="glyphicon glyphicon-check" style={{ color: '#006e78', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
            ) : (
              <Glyphicon glyph="glyphicon glyphicon-unchecked" style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
            )}
          </li>
        )
      }
    })
    permissions.push(
      <li key="contactPerson" onClick={() => togglePermission(lock, 'contactPerson')} style={{ textDecoration: 'none', cursor: 'pointer' }}>
        {t('Administrator')}
        {permissionIsToggled(lock._id, 'contactPerson') ? (
          <Glyphicon glyph="glyphicon glyphicon-check" style={{ color: '#006e78', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
        ) : (
          <Glyphicon glyph="glyphicon glyphicon-unchecked" style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
        )}
      </li>
    )

    return permissions
  }
  const renderLockElement = (lock, isSelected) => (
    <div key={lock._id}>
      <hr />
      <Row style={{ display: 'block', margin: 'auto' }} className="userFormListItem">
        <Col xs={12} sm={12} lg={2} md={2} className="DoorType">
          <b>{lock.custom_name ? lock.custom_name : ''}</b>
        </Col>
        <Col xs={12} sm={12} lg={2} md={2} className="DoorType">
          <b>{lock.address.street ? lock.address.street : ''}</b>
        </Col>

        <Col xs={12} sm={12} lg={6} md={6} className="DoorType">
          <ol style={{ listStyleType: 'none', opacity: isSelected ? 1 : 0.5 }}>
            <li>{t('Available functionalities:')}</li>
            {renderPermissions(lock)}
          </ol>
        </Col>

        <Col xs={2} style={{ display: 'inline-block', textAlign: 'right' }}>
          {isSelected ? (
            <div className="Selected" onClick={() => toggleLock(lock)}>
              <Glyphicon glyph="glyphicon glyphicon-check" style={{ color: '#006e78', marginRight: '5px', marginLeft: '5px' }} />
              <div>{t('Unselect')}</div>
            </div>
          ) : (
            <div className="Select" onClick={() => toggleLock(lock)}>
              <Glyphicon glyph="glyphicon glyphicon-unchecked" style={{ color: '#006e78', marginRight: '5px', marginLeft: '5px' }} />
              <div>{t('Select')}</div>
            </div>
          )}
        </Col>
      </Row>
    </div>
  )

  const renderLocks = () => {
    const lockElements = Object.values(keyedLocks).map((lock) => ({
      lock,
      isSelected: selectedLocks.some((selectedLock) => selectedLock._id === lock._id),
    }))

    lockElements.sort((a, b) => {
      if (a.isSelected === b.isSelected) {
        const nameA = a.lock.name || ''
        const nameB = b.lock.name || ''
        return nameA.localeCompare(nameB)
      }
      return a.isSelected ? -1 : 1
    })

    return lockElements.map(({ lock, isSelected }) => renderLockElement(lock, isSelected))
  }

  const uniqueCompaniesClientNumber = useMemo(() => {
    if (!isHandleStorageSupplierClientsModalOpen) {
      return []
    }

    const uniqueCompanies = users.reduce((acc, user) => {
      const existingCompany = acc.find((company) => company.value === user.selectedCompany.value)

      if (!existingCompany && user.selectedCompany.value) {
        acc.push(user.selectedCompany)
      }

      return acc
    }, [])

    return uniqueCompanies
  }, [isHandleStorageSupplierClientsModalOpen, users])

  return (
    <Modal ariaHideApp={false} isOpen={modalIsOpen} onRequestClose={closeModal} style={customStyles}>
      <Grid fluid>
        <Row>
          <Col xs={12} sm={12} md={12} lg={12} style={{ textAlign: 'right' }}>
            <div className="qCloseButton" onClick={closeModal}>
              {t('Close')}
            </div>
          </Col>
        </Row>

        <Row>
          <Col xs={12} sm={12} md={12} lg={12} style={{ textAlign: 'center' }}>
            <div style={{ width: '100%' }} className="userFormAccessTitle">
              {t('Whom do you want to invite?')}
            </div>
          </Col>
        </Row>

        <br />

        <Row>
          <Col xs={12} sm={12} md={3} style={{ display: 'flex', flexDirection: 'column' }}>
            <div>
              {t('Mobile number')} <span className="Obligatorisk"> *</span>
            </div>
            <PhoneInput
              defaultCountry={country}
              value={phone}
              placeholder={countryPhoneFormatMapping[country] ? countryPhoneFormatMapping[country] : 'ex. 0701234567'}
              onCountryChange={setCountry}
              addInternationalOption={false}
              onChange={(p) => {
                if (p) {
                  setPhone(p)
                }
              }}
            />
          </Col>
          <Col xs={12} sm={12} md={3} lg={3} style={{ margin: 0 }}>
            <div>
              {t('Company:')} <span className="Obligatorisk"> *</span>
            </div>
            <NewUserCompany
              preselectedCompany={
                selectedCompany && {
                  _id: selectedCompany.value,
                  name: selectedCompany.label,
                }
              }
              companySelected={setSelectedCompany}
            />
            <br />
          </Col>
          <Col xs={12} sm={12} md={3} lg={3} className="Beskrivning">
            <span className="BeskrivningTitel">{t('Additional phone number:')} </span>
            <input onChange={(e) => setAlternativePhone(e.target.value)} name="alternativePhone" className="inputDescr" type="text" value={alternativePhone} placeholder="" style={{ width: '50%', border: 'none', borderBottom: '1px solid lightgrey', textAlign: 'left' }} />
            <OverlayTrigger key={'top'} placement={'top'} overlay={<Tooltip id={'tooltip-top'}>{t('If the person is using his/her work phone for ordering, provide the number here')}</Tooltip>}>
              <Glyphicon glyph="glyphicon glyphicon-question-sign"></Glyphicon>
            </OverlayTrigger>
          </Col>

          <Col xs={12} md={1} lg={1}>
            &nbsp;
          </Col>

          <Col xs={12} md={2} lg={2} onClick={addUser} className="AddUserBtn">
            <div style={{ textAlign: 'center' }} className="AddUserText">
              {t('+ Add more')}
            </div>
          </Col>
        </Row>

        <br />

        <Row style={{ display: 'block', margin: 'auto' }}>
          <Col md={12}>
            {users.length ? (
              <div>
                <Row>
                  <Col xs={12} sm={5} md={5} lg={5}>
                    <h5 style={{ color: '#006e78' }}>{t('Mobile number')}</h5>
                  </Col>
                  <Col xs={12} sm={5} md={5} lg={5}>
                    <h5 style={{ color: '#006e78' }}>{t('Company')}</h5>
                  </Col>
                  <Col xs={12} sm={1} md={1} lg={1}></Col>
                </Row>

                {users.map((user) => (
                  <Row key={user.phone}>
                    <Col xs={12} sm={5} md={5} lg={5}>
                      {user.phone}
                    </Col>
                    <Col xs={12} sm={5} md={5} lg={5}>
                      {user.selectedCompany.label}
                    </Col>
                    <Col xs={12} sm={1} md={1} lg={1}>
                      <div onClick={() => removeUser(user)} style={{ marginLeft: '10px', textAlign: 'center' }} className="remove-subject">
                        X
                      </div>
                    </Col>
                  </Row>
                ))}
              </div>
            ) : null}
          </Col>
        </Row>

        <br />

        <Row style={{ width: '100%', display: 'block', margin: 'auto', marginTop: 20 }}>
          <Col md={12}>
            <div style={{ width: '100%' }} className="userFormAccessTitle">
              {t('What do you want to invite to?')}&nbsp;{selectedLocks.length > 0 && `(${selectedLocks.length} ${t('selected')})`}
            </div>
          </Col>
          <Col md={12}>
            <div style={{ maxHeight: 390, overflowY: 'scroll', width: '100%', marginBottom: '20px' }} className="userFormList">
              {renderLocks()}
            </div>
          </Col>
        </Row>

        <Row style={{ marginTop: 20 }}>
          <Col lg={12} md={12} xs={12}>
            <div onClick={submit} className="submitUserForm">
              {t('Send')}
            </div>
          </Col>
        </Row>
      </Grid>
      <HandleStorageSupplierClientsModal
        isOpen={isHandleStorageSupplierClientsModalOpen}
        closeModal={() => setIsHandleStorageSupplierClientsModalOpen(false)}
        preselectedCompanies={uniqueCompaniesClientNumber}
        suppliers={storageSuppliers}
        updateSuppliers={submitNewSuppliers}
        t={t}
      />
    </Modal>
  )
}

export default NewUserModal
